import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { usePortfolioFilters } from "../../context/PortfolioFilterContext";
import { SectionLabel } from "../general/SectionLabel";
import ClaimTypeChip from "../issues/ClaimTypeChip";
import { PortfolioCaseCount } from "./PortfolioCaseCount";

/**
 * A filter of the portfolio as a button that opens a list under itself: the
 * choice of no filter at all, then every type with the number of its cases.
 */
function FilterMenu<Type extends string>({
  buttonLabel,
  allLabel,
  options,
  typeLabel,
  onSelect,
  isDisabled,
}: {
  // says how many of the types are selected
  buttonLabel: string;
  allLabel: string;
  options: { type: Type; selected: boolean; casesCount: number }[];
  typeLabel: (type: Type) => ReactNode;
  // called with the types that are selected, or with none to drop the filter
  onSelect: (types?: Type[]) => void;
  isDisabled: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isAnySelected = options.some(({ selected }) => selected);

  const toggle = (toggled: Type) => {
    const selectedTypes = options
      .filter(({ type, selected }) => (type === toggled ? !selected : selected))
      .map(({ type }) => type);
    onSelect(selectedTypes.length > 0 ? selectedTypes : undefined);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="true"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 1.75,
          borderColor: "divider",
          "& .MuiButton-endIcon": { ml: 0.5, color: "text.secondary" },
        }}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <FormGroup sx={{ px: 2, py: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!isAnySelected}
                disabled={isDisabled}
                // there is a filter to drop only while a type is selected
                onChange={() => isAnySelected && onSelect()}
              />
            }
            label={allLabel}
          />
          <Divider />
          {options.map(({ type, selected, casesCount }) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={selected}
                  disabled={isDisabled}
                  onChange={() => toggle(type)}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {typeLabel(type)}
                  <Typography component="span" sx={{ color: "text.secondary" }}>
                    ({casesCount})
                  </Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      </Popover>
    </>
  );
}

/**
 * The filters of the portfolio as a quiet strip over its pages, and how many
 * of the cases they let through.
 */
export const PortfolioFilterBar = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "PortfolioFilterBar",
  });
  // the settlements section offers no settlement filter; its route loader
  // drops any settlement param from the URL
  const showSettlementsFilter = !useLocation().pathname.startsWith(
    "/portfolio/settlements",
  );

  const {
    claimTypes,
    selectClaimTypes,
    agreementTypes,
    selectAgreementTypes,
    settlementTypes,
    selectSettlementTypes,
    // the cases are being queried anew for a changed filter
    queryOngoing,
  } = usePortfolioFilters();

  const selectedOf = (options: { selected: boolean }[]) =>
    options.filter(({ selected }) => selected).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 1.25,
        rowGap: 1,
        borderRadius: 2.5,
        px: 1.5,
        py: 1,
      }}
    >
      <Box sx={{ mr: 0.5 }}>
        <SectionLabel>{t("filter")}</SectionLabel>
      </Box>

      <FilterMenu
        buttonLabel={t("claimTypeFilter.counter", {
          count: selectedOf(claimTypes),
          total: claimTypes.length,
        })}
        allLabel={t("claimTypeFilter.counter", { count: 0 })}
        options={claimTypes}
        typeLabel={(type) => (
          <ClaimTypeChip type={type} disabled={queryOngoing} />
        )}
        onSelect={selectClaimTypes}
        isDisabled={queryOngoing}
      />

      <FilterMenu
        buttonLabel={t("agreementTypeFilter.counter", {
          count: selectedOf(agreementTypes),
          total: agreementTypes.length,
        })}
        allLabel={t("agreementTypeFilter.counter", { count: 0 })}
        options={agreementTypes}
        typeLabel={(type) => (
          <Typography component="span">
            {t(`agreementTypeFilter.agreementTypes.${type}`)}
          </Typography>
        )}
        onSelect={selectAgreementTypes}
        isDisabled={queryOngoing}
      />

      {showSettlementsFilter && (
        <FilterMenu
          buttonLabel={t("settlementTypeFilter.counter", {
            count: selectedOf(settlementTypes),
            total: settlementTypes.length,
          })}
          allLabel={t("settlementTypeFilter.counter", { count: 0 })}
          options={settlementTypes}
          typeLabel={(type) => (
            <Typography component="span">
              {t(`settlementTypeFilter.settlementTypes.${type}`)}
            </Typography>
          )}
          onSelect={selectSettlementTypes}
          isDisabled={queryOngoing}
        />
      )}

      <Box sx={{ ml: "auto" }}>
        <PortfolioCaseCount />
      </Box>
    </Paper>
  );
};
